import React from 'react'
import { Users } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { userService } from '@/services/userService'
import { managerAuthService } from '@/services/managerAuthService'
import { Admin } from '@/types/company'
import { AddAdminModal } from '@/components/admin/AddAdminModal'
import { EditAdminModal } from '@/components/admin/EditAdminModal'
import { UserStats } from '@/components/user/UserStats'
import { UserFilters } from '@/components/user/UserFilters'
import { UserTable } from '@/components/user/UserTable'
import { Skeleton } from '@/components/ui/skeleton'

export default function UserManagementPage() {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = React.useState(true)
  const [admins, setAdmins] = React.useState<Admin[]>([])
  const [searchQuery, setSearchQuery] = React.useState('')
  const [filterType, setFilterType] = React.useState<'all' | 'super_admin' | 'manager'>('all')
  const [showAddUserModal, setShowAddUserModal] = React.useState(false)
  const [editingAdmin, setEditingAdmin] = React.useState<Admin | null>(null)

  // Yöneticileri getir
  const fetchAdmins = React.useCallback(async () => {
    try {
      const users = await userService.getAllUsers()
      const filteredAdmins = users.filter(user => 
        user.role === 'super_admin' || user.role === 'manager'
      ) as Admin[]
      setAdmins(filteredAdmins)
    } catch (error) {
      console.error('Yöneticiler getirilirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Yöneticiler getirilirken bir hata oluştu.'
      })
      setAdmins([])
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  // Manager paneline direkt giriş
  const handleDirectLogin = async (managerId: string) => {
    try {
      const token = await managerAuthService.getLoginToken(managerId)
      const loginUrl = managerAuthService.getLoginUrl(token)
      window.open(loginUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Giriş yapılırken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Giriş yapılırken bir hata oluştu.'
      })
    }
  }

  // Kullanıcı silme
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) {
      return
    }

    try {
      await userService.deleteUser(userId)
      await fetchAdmins()
      toast({
        title: 'Başarılı',
        description: 'Kullanıcı başarıyla silindi.'
      })
    } catch (error) {
      console.error('Kullanıcı silinirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Kullanıcı silinirken bir hata oluştu.'
      })
    }
  }

  // Manager olarak devam et
  const handleSwitchToManager = async (managerId: string) => {
    try {
      const token = await managerAuthService.getLoginToken(managerId)
      const loginUrl = managerAuthService.getLoginUrl(token)
      window.location.href = loginUrl // Mevcut sekmede aç
    } catch (error) {
      console.error('Giriş yapılırken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Giriş yapılırken bir hata oluştu.'
      })
    }
  }

  // Sayfa yüklendiğinde yöneticileri getir
  React.useEffect(() => {
    fetchAdmins()
  }, [fetchAdmins])

  // Filtrelenmiş yöneticiler
  const filteredAdmins = React.useMemo(() => {
    return admins.filter(admin => {
      const matchesSearch = 
        admin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        admin.email.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesFilter = 
        filterType === 'all' ||
        admin.role === filterType

      return matchesSearch && matchesFilter
    })
  }, [admins, searchQuery, filterType])

  if (isLoading) {
    return (
      <div className="h-full flex-1">
        <div className="flex-1 space-y-6 p-8 pt-6">
          {/* Hero Section Skeleton */}
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <div className="h-56 bg-gradient-to-r from-emerald-600 to-emerald-400 p-8">
              <div className="flex justify-between items-start relative w-full h-full">
                <div className="space-y-3 flex-1">
                  <Skeleton className="h-4 w-40 bg-white/20" />
                  <Skeleton className="h-9 w-80 bg-white/30" />
                  <Skeleton className="h-16 w-[500px] bg-white/20" />
                </div>
                <Skeleton className="w-40 h-40 rounded-lg bg-white/10" />
              </div>
            </div>
          </div>

          {/* İstatistik Kartları Skeleton - Gerçek kart boyutlarında */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="overflow-hidden border rounded-lg shadow bg-white">
                <div className="p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <Skeleton className="h-5 w-[120px] mb-1" />
                      <Skeleton className="h-9 w-[70px]" />
                    </div>
                    <Skeleton className="h-12 w-12 rounded-xl" />
                  </div>
                  
                  {/* Kartların altına dekoratif çizgi */}
                  <div className="mt-4 pt-3 border-t">
                    <Skeleton className="h-4 w-[150px]" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Filtreler Skeleton - Gerçek filtre alanı boyutlarında */}
          <div className="rounded-lg border shadow-sm bg-white p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-[100px]" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-[100px]" />
                <Skeleton className="h-10 w-full" />
              </div>
              <Skeleton className="h-10 w-[120px]" />
            </div>
          </div>

          {/* Tablo Skeleton - Gerçek tablo yapısında */}
          <div className="rounded-lg border overflow-hidden bg-white shadow">
            <div className="p-4 bg-slate-50">
              <div className="grid grid-cols-5 gap-4">
                <Skeleton className="h-6 w-[140px]" />
                <Skeleton className="h-6 w-[120px]" />
                <Skeleton className="h-6 w-[120px]" />
                <Skeleton className="h-6 w-[100px]" />
                <Skeleton className="h-6 w-[90px]" />
              </div>
            </div>
            <div className="divide-y">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="p-5">
                  <div className="grid grid-cols-5 gap-4">
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-[150px]" />
                      <Skeleton className="h-4 w-[100px]" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-[120px]" />
                      <Skeleton className="h-4 w-[90px]" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-[100px]" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-6 w-[110px]" />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Skeleton className="h-9 w-9 rounded-md" />
                      <Skeleton className="h-9 w-9 rounded-md" />
                      <Skeleton className="h-9 w-9 rounded-md" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sayfalama Skeleton - Gerçek boyutlarda */}
          <div className="flex items-center justify-between mt-4 pb-4">
            <Skeleton className="h-6 w-[200px]" />
            <div className="flex items-center gap-1">
              <Skeleton className="h-10 w-[90px]" />
              <div className="flex gap-1">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-10 rounded" />
                ))}
              </div>
              <Skeleton className="h-10 w-[90px]" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex-1">
      <div className="flex-1 space-y-6 p-8 pt-6">
        {/* Hero Section */}
        <div className="rounded-2xl overflow-hidden shadow-lg">
          <div
            className="flex"
            style={{
              background: 'linear-gradient(to right, #059669, #10b981)',
            }}
          >
            <div className="p-8 flex justify-between items-start relative w-full">
              <div className="space-y-3 flex-1">
                <div className="text-white/80 text-sm font-medium">
                  Kullanıcı Yönetim Paneli
                </div>
                <h1 className="text-3xl font-bold text-white">
                  Yönetici ve Kullanıcı Kontrolü
                </h1>
                <p className="text-white/90 text-sm max-w-xl leading-relaxed">
                  Super adminler ve firma yöneticilerini görüntüleyin, düzenleyin ve
                  yönetin. Kullanıcı rollerini kontrol edin, yeni yönetici ekleyin
                  veya mevcut kullanıcıların bilgilerini güncelleyin.
                </p>
              </div>
              <div
                className="w-40 h-40 rounded-lg overflow-hidden relative z-10 flex items-center justify-center ml-8"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }}
              >
                <Users className="w-20 h-20 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>

        {/* İstatistikler */}
        <UserStats users={admins} />

        {/* Filtreler */}
        <UserFilters
          onSearchChange={setSearchQuery}
          onFilterChange={setFilterType}
          filterValue={filterType}
          onAddUser={() => setShowAddUserModal(true)}
        />

        {/* Kullanıcı Tablosu */}
        <UserTable
          users={filteredAdmins}
          onDirectLogin={handleDirectLogin}
          onDelete={handleDeleteUser}
          onSwitchToManager={handleSwitchToManager}
          onEdit={(admin) => setEditingAdmin(admin)}
        />

        {/* Yönetici Ekleme Modal */}
        {showAddUserModal && (
          <AddAdminModal
            onClose={() => setShowAddUserModal(false)}
            onSubmit={async (data) => {
              try {
                await userService.createUser({
                  ...data,
                  role: 'super_admin'
                })
                await fetchAdmins()
                toast({
                  title: 'Başarılı',
                  description: 'Yönetici başarıyla eklendi.'
                })
                setShowAddUserModal(false)
              } catch (error) {
                console.error('Yönetici eklenirken hata:', error)
                toast({
                  variant: 'destructive',
                  title: 'Hata',
                  description: 'Yönetici eklenirken bir hata oluştu.'
                })
              }
            }}
          />
        )}

        {/* Yönetici Düzenleme Modal */}
        {editingAdmin && (
          <EditAdminModal
            admin={editingAdmin}
            isOpen={true}
            onClose={() => setEditingAdmin(null)}
            onSuccess={fetchAdmins}
          />
        )}
      </div>
    </div>
  )
}
